import React, { useEffect, useState } from 'react';
import {
  adminGetFeedback,
  adminMarkFeedback,
  callableErrorMessage,
  confirmAction,
  copyText,
  fillUserAddresses,
  userPhotoCountLabel,
  userPhotoList
} from '../../../adminApi';

function previewText(message) {
  const text = String(message || '').replace(/\s+/g, ' ').trim();
  if (!text) {
    return 'No message';
  }
  return text.length > 160 ? `${text.slice(0, 160)}…` : text;
}

function formatFeedbackTime(timestamp) {
  const value = Number(timestamp);
  if (!value) {
    return '';
  }
  const date = new Date(value * 1000);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString();
}

function FeedbackPanel(props) {
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState({});
  const [selectedId, setSelectedId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const itemCount = entries.reduce((total, entry) => total + ((entry.items && entry.items.length) || 0), 0);

  const load = () => {
    setBusy(true);
    setError('');
    adminGetFeedback()
      .then((result) => {
        const nextEntries = (result && result.feedback) || [];
        const nextUsers = (result && result.users) || {};
        setEntries(nextEntries);
        setUsers(nextUsers);
        setSelectedId((current) => {
          if (current && nextEntries.some((entry) => entry.userId === current)) {
            return current;
          }
          return nextEntries[0] ? nextEntries[0].userId : '';
        });
        if (props.onChanged) {
          props.onChanged(nextEntries.reduce((total, entry) => total + ((entry.items && entry.items.length) || 0), 0));
        }
        fillUserAddresses(nextUsers, setUsers, props.onUsersUpdated);
      })
      .catch((err) => setError(callableErrorMessage(err)))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = entries.find((entry) => entry.userId === selectedId);
  const user = selected ? users[selected.userId] : null;
  const photo = userPhotoList(user)[0];

  const markReviewed = (items, label) => {
    const next = (items || []).filter((item) => item && item.userId && item.feedbackId);
    if (next.length === 0) {
      return;
    }
    if (!confirmAction(label)) {
      return;
    }
    setBusy(true);
    setError('');
    adminMarkFeedback(next)
      .then(() => load())
      .catch((err) => {
        setError(callableErrorMessage(err));
        setBusy(false);
      });
  };

  return (
    <section>
      <div className="admin-section-head">
        <div>
          <h2>Feedback</h2>
          <p className="admin-muted">
            {itemCount} unread message{itemCount === 1 ? '' : 's'} from {entries.length} user{entries.length === 1 ? '' : 's'}
          </p>
        </div>
        <button type="button" className="admin-btn" onClick={load} disabled={busy}>Refresh</button>
      </div>
      {error ? <p className="admin-error">{error}</p> : null}
      {entries.length === 0 && !busy ? (
        <p className="admin-empty">No feedback to review.</p>
      ) : (
        <div className="admin-split">
          <div className="admin-list">
            {entries.map((entry) => {
              const account = users[entry.userId];
              const first = (entry.items || [])[0];
              return (
                <button
                  type="button"
                  key={entry.userId}
                  className={entry.userId === selectedId ? 'admin-list-item is-active' : 'admin-list-item'}
                  onClick={() => setSelectedId(entry.userId)}
                >
                  <strong>{account ? account.name : 'Unknown'}</strong>
                  <p className="admin-muted">
                    {entry.items.length} message{entry.items.length === 1 ? '' : 's'}
                    {first && first.timestamp ? ` · ${formatFeedbackTime(first.timestamp)}` : ''}
                  </p>
                  <p className="admin-muted">{previewText(first && first.message)}</p>
                </button>
              );
            })}
          </div>
          {selected ? (
            <section className="admin-detail">
              <div className="admin-user">
                {photo ? <img src={photo.url} alt="" /> : <div className="admin-card__empty" style={{ width: 72, height: 72 }}>No photo</div>}
                <div>
                  <strong>
                    {user ? `${user.name || 'Unknown'}${user.age != null ? ` | ${user.age}` : ''}` : selected.userId}
                  </strong>
                  <p className="admin-muted">{user && user.active === 'false' ? 'Disabled' : 'Active'}</p>
                  <p className="admin-muted">{user ? userPhotoCountLabel(user) : ''}</p>
                </div>
              </div>
              {(selected.items || []).map((item, index) => (
                <div key={item.feedbackId || index} className="admin-email">
                  <div className="admin-section-head">
                    <p className="admin-muted">
                      Message {index + 1}
                      {item.timestamp ? ` · ${formatFeedbackTime(item.timestamp)}` : ''}
                    </p>
                    {item.feedbackId ? (
                      <button
                        type="button"
                        className="admin-btn"
                        disabled={busy}
                        onClick={() => markReviewed(
                          [item],
                          'Mark this message as reviewed? It will leave the queue.'
                        )}
                      >
                        Mark reviewed
                      </button>
                    ) : null}
                  </div>
                  <pre>{item.message || 'No message'}</pre>
                </div>
              ))}
              <div className="admin-actions">
                <button
                  type="button"
                  className="admin-btn admin-btn--primary"
                  disabled={busy}
                  onClick={() => markReviewed(
                    selected.items,
                    `Mark all ${selected.items.length} message(s) as reviewed? They will leave the queue.`
                  )}
                >
                  Mark all reviewed
                </button>
                {user ? (
                  <button type="button" className="admin-btn" onClick={() => props.onPreview(user)}>
                    Preview
                  </button>
                ) : null}
                <button type="button" className="admin-btn" onClick={() => copyText(selected.userId)}>UserID</button>
              </div>
            </section>
          ) : (
            <p className="admin-empty">Pick a message to review it.</p>
          )}
        </div>
      )}
      {busy ? <p className="admin-muted">Working…</p> : null}
    </section>
  );
}

export default FeedbackPanel;
